import {
  Flex,
  type FlexProps,
  ReportSection,
  ReportSectionItemContent,
} from '@/components';
import type { LedgerKsInspectionEvaluationSectionProps } from '@/features/mold/models/ledger';
import { DiscriminatedInspectionEvaluationModel } from '@/features/project/projects/_models/project';
import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { LEDGER_BORDER } from '../../../utils';

export const KsInspectionEvaluationSection: FC<
  LedgerKsInspectionEvaluationSectionProps
> = ({ project }) => {
  const { t } = useTranslation();

  const parsed = DiscriminatedInspectionEvaluationModel.safeParse(project);

  if (!parsed.success || parsed.data.sheetType !== 'KS') {
    return null;
  }

  const { inspectionEvaluation } = parsed.data;

  return (
    <ReportSection
      title={t('inspection-evaluation')}
      items={[
        {
          subtitle: t('inspection-and-improvement-by-fund-jurisdiction'),
          item: (
            <Flex vertical>
              <InspectionEvaluationContentItem
                label={t('inspection-result')}
                value={
                  inspectionEvaluation
                    .inspectionAndImprovementByAdministrativeJurisdiction
                    ?.inspectionResult
                }
              />
              <InspectionEvaluationContentItem
                label={t('evaluation')}
                value={
                  inspectionEvaluation
                    .inspectionAndImprovementByAdministrativeJurisdiction
                    ?.evaluation
                }
              />
              <InspectionEvaluationContentItem
                label={t('direction-of-improvement')}
                value={
                  inspectionEvaluation
                    .inspectionAndImprovementByAdministrativeJurisdiction
                    ?.directionOfImprovement
                }
                isLast
              />
            </Flex>
          ),
        },
        {
          subtitle: t('findings-of-external-experts'),
          isLongText: true,
          item: (
            <InspectionEvaluationContentItem
              value={inspectionEvaluation.findingsOfExternalExperts}
              isLast
            />
          ),
        },
        {
          subtitle: t(
            'process-leading-to-the-findings-of-review-promotion-team',
          ),
          isLongText: true,
          item: (
            <InspectionEvaluationContentItem
              value={
                inspectionEvaluation.processLeadingToTheFindingsOfReviewPromotionTeam
              }
              isLast
            />
          ),
        },
        {
          subtitle: t('improvement-point-based-on-the-findings'),
          isLongText: true,
          item: (
            <InspectionEvaluationContentItem
              value={inspectionEvaluation.improvementPointBasedOnTheFindings}
              isLast
            />
          ),
        },
      ]}
    />
  );
};

const InspectionEvaluationContentItem: FC<{
  label?: string;
  value: ReactNode;
  wrapperStyle?: FlexProps['style'];
  isLast?: boolean;
}> = ({ label, value, isLast, wrapperStyle }) => {
  const inspectionEvaluationLabelStyle = {
    width: 150,
    minWidth: 150,
  };
  return (
    <ReportSectionItemContent
      label={label}
      value={value}
      labelStyle={inspectionEvaluationLabelStyle}
      wrapperStyle={{
        borderBottom: isLast ? undefined : LEDGER_BORDER,
        height: 'auto',
        ...wrapperStyle,
      }}
    />
  );
};
